// src/theme/animation-config.ts

export type AnimationConfig = {
  id: string;
  type: 'floating' | 'swimming' | 'rising' | 'falling';
  enabled: boolean;
  count: number; // how many instances
  speed: number; // multiplier for animation speed
  size: { min: number; max: number };
  positions?: Array<{ x: string; y: string }>; // predefined positions
};

export type ThemeAnimations = {
  characters: AnimationConfig[];
  bubbles: AnimationConfig | null;
  decorations: AnimationConfig[];
};

// Default animation configs for each theme
export const THEME_ANIMATIONS: Record<string, ThemeAnimations> = {
  'ocean-turtle': {
    characters: [
      {
        id: 'turtle',
        type: 'swimming',
        enabled: true,
        count: 3,
        speed: 1,
        size: { min: 40, max: 80 },
        positions: [
          { x: '10%', y: '60%' },
          { x: '85%', y: '50%' },
          { x: '50%', y: '70%' },
        ],
      },
    ],
    bubbles: {
      id: 'bubbles',
      type: 'rising',
      enabled: true,
      count: 25,
      speed: 0.8,
      size: { min: 15, max: 35 },
    },
    decorations: [
      {
        id: 'coral',
        type: 'floating',
        enabled: false,
        count: 4,
        speed: 0.8,
        size: { min: 30, max: 50 },
        positions: [
          { x: '5%', y: '80%' },
          { x: '20%', y: '85%' },
          { x: '75%', y: '82%' },
          { x: '90%', y: '78%' },
        ],
      },
    ],
  },
  'mountain-eagle': {
    characters: [
      {
        id: 'eagle',
        type: 'floating',
        enabled: true,
        count: 2,
        speed: 1.2,
        size: { min: 60, max: 90 },
        // No predefined positions - eagles will spawn randomly (handled in AnimationLayer)
      },
    ],
    bubbles: null,
    decorations: [
      {
        id: 'mountain',
        type: 'floating',
        enabled: true,
        count: 3,
        speed: 0.5,
        size: { min: 60, max: 100 },
        positions: [
          { x: '10%', y: '75%' },
          { x: '50%', y: '80%' },
          { x: '85%', y: '78%' },
        ],
      },
    ],
  },
  'sun-fire': {
    characters: [
      {
        id: 'firebird',
        type: 'floating',
        enabled: true,
        count: 3,
        speed: 1.5,
        size: { min: 50, max: 80 },
        positions: [
          { x: '15%', y: '40%' },
          { x: '60%', y: '35%' },
          { x: '80%', y: '55%' },
        ],
      },
    ],
    bubbles: null,
    decorations: [
      {
        id: 'sun',
        type: 'floating',
        enabled: true,
        count: 1,
        speed: 0.3,
        size: { min: 100, max: 120 },
        positions: [
          { x: '85%', y: '10%' },
        ],
      },
    ],
  },
  'lightning-bolt': {
    characters: [
      {
        id: 'bolt',
        type: 'falling',
        enabled: true,
        count: 4,
        speed: 2,
        size: { min: 40, max: 70 },
      },
    ],
    bubbles: null,
    decorations: [],
  },
  'air-clouds': {
    characters: [
      {
        id: 'cloud',
        type: 'floating',
        enabled: true,
        count: 5,
        speed: 0.8,
        size: { min: 50, max: 90 },
      },
    ],
    bubbles: null,
    decorations: [],
  },
};

export const getThemeAnimations = (themeKey: string): ThemeAnimations => {
  const theme = THEME_ANIMATIONS[themeKey] || THEME_ANIMATIONS['ocean-turtle'];
  // Return a deep clone to ensure React.memo detects changes when theme switches
  return JSON.parse(JSON.stringify(theme));
};
